import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Map } from 'immutable';
import { RTCSession } from 'jssip/lib/RTCSession';
import {
    answerCall,
    hangupSession,
    holdCall,
    swapCall,
    transferAttendedCall,
    transferBlindCall,
    unholdCall,
} from 'flow/actions/phone-actions';
import { mergeCall } from 'flow/actions/top-actions';
import CallTransferDialog from 'flow/components/call-transfer-dialog/CallTransferDialog';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import useCustomer from 'flow/hooks/useCustomer';
import {
    canMerge,
    canSwapSessions,
    getNumLocalCalls,
    getOwnActiveCallByCallId,
    getPaiHeaderFromLocalCall,
    isSessionOnHold,
} from 'flow/selectors/call-selectors';
import { selectTransferInDoubleCallEnabled } from 'flow/selectors/feature-flag-selectors';
import { Extension } from 'flow/types/Extension';
import { CallTransferQueue } from 'flow/types/Pbx';
import { isExtensionStatusInCall } from 'flow/utils';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { convertNumberToE164, isQueueCall } from 'flow/utils/call-utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import CallItem from './CallItem';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

const transferLogLocation = 'call item';

interface Props {
    session: RTCSession;
    callId: string;
    spawnedNotifications: string[];
    onNotificationSpawned: (notificationDedupKey: string) => void;
}

const LocalCall = ({
    session: initialSession,
    callId,
    spawnedNotifications,
    onNotificationSpawned,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const { ownContact } = useContact();
    const { ownCustomer } = useCustomer();

    useEffect(() => {
        addUserPhoneLogEntry(`LocalCall component mounted, callid: ${callId}`);
        return () => {
            addUserPhoneLogEntry(
                `LocalCall component unmounted, callid: ${callId}`,
            );
        };
    }, [callId]);

    const activeTopCall = useAppSelector((state) =>
        getOwnActiveCallByCallId(state, callId),
    );

    const [session] = useState<RTCSession>(initialSession);
    const [isAccepted, setIsAccepted] = useState(session.isEstablished());
    const [startTime, setStartTime] = useState(session.start_time);
    const [isEnded, setIsEnded] = useState(false);
    const [failCause, setFailCause] = useState(undefined);
    const [selectedExtensionToTransferTo, setselectedExtensionToTransferTo] =
        useState<{ key: string; contact: Extension; status: string }>();

    const [selectedPbxToTransferTo, setSelectedPbxToTransferTo] =
        useState<CallTransferQueue | null>();
    const [showCallTransferDialog, setShowCallTransferDialog] = useState(false);

    const remoteIdentityUser = session.remote_identity.uri.user;
    const remoteNumberFromTop = useMemo(() => {
        if (activeTopCall?.has('direction')) {
            return activeTopCall?.get('direction') === 'remote_start'
                ? activeTopCall.get('destination')
                : activeTopCall?.get('source');
        }

        return remoteIdentityUser;
    }, [activeTopCall, remoteIdentityUser]);

    const [remoteNumber, setRemoteNumber] = useState(
        remoteNumberFromTop || session.remote_identity.uri.user,
    );
    const [hasNumberFromTop, setHasNumberFromTop] = useState(false);
    const [selectedTransferNumber, setSelectedTransferNumber] =
        useState(undefined);
    const callIsQueueCall = isQueueCall(session);

    useEffect(() => {
        const onAccepted = () => {
            setStartTime(session.start_time);
            setIsAccepted(true);
        };
        const onEnded = () => setIsEnded(true);

        session.on('accepted', onAccepted);
        session.on('ended', onEnded);
        session.on('failed', (data) => setFailCause(data.cause));

        return () => {
            session.removeListener('accepted', onAccepted);
            session.removeListener('ended', onEnded);
            session.removeListener('failed', (data) =>
                setFailCause(data.cause),
            );
        };
    }, [session]);

    // We save the number in the state so we can use it during the exit transition when the
    // active call (that contains the number) has been removed from the store.
    useEffect(() => {
        if (remoteNumberFromTop) {
            setRemoteNumber(remoteNumberFromTop);
            setHasNumberFromTop(true);
        }
    }, [remoteNumberFromTop]);

    const swapFunction = useCallback(() => {
        addUserPhoneLogEntry(`Swap clicked for call with id ${callId}`);
        dispatch(swapCall(callId));
    }, [dispatch, callId]);

    const canSwap = useAppSelector((state) => canSwapSessions(state, callId));
    const onSwap = canSwap ? swapFunction : undefined;

    const holdFunction = useCallback(() => {
        addUserPhoneLogEntry(`Hold clicked for call with id ${callId}`);
        dispatch(holdCall(session));
    }, [dispatch, session, callId]);

    const isOnHold = useAppSelector((state) => isSessionOnHold(state, callId));
    const onHold = isAccepted && !isOnHold ? holdFunction : undefined;

    const unholdFunction = useCallback(() => {
        addUserPhoneLogEntry(`Unhold clicked for call with id ${callId}`);
        dispatch(unholdCall(session));
    }, [dispatch, session, callId]);
    const onUnhold = isAccepted && isOnHold ? unholdFunction : undefined;

    /*
     * AC-811: Guard the UI answer handler against duplicate dispatches.
     * When a headset is connected, pressing the green button can cause both
     * the UI click and the headset SDK's onAcceptCall to fire answerCall.
     * By gating on isAccepted we ensure the callback is only available while
     * the session is genuinely waiting for an answer, and becomes undefined
     * once answered — matching the existing pattern used for onHold/onUnhold.
     */
    const answerFunction = useCallback(() => {
        addUserPhoneLogEntry(`Answer clicked for call with id ${callId}`);
        dispatch(answerCall(session));
    }, [dispatch, session, callId]);
    const onAnswer = !isAccepted ? answerFunction : undefined;

    const onHangup = useCallback(() => {
        addUserPhoneLogEntry(`Hangup clicked for call with id ${callId}`);
        dispatch(hangupSession(session));
    }, [dispatch, session, callId]);

    const mergeFunction = useCallback(() => {
        addUserPhoneLogEntry(`Merge clicked for call with id ${callId}`);
        dispatch(mergeCall());
    }, [dispatch, callId]);

    const isMergable = useAppSelector((state) => canMerge(state, callId));
    const onMerge = isMergable ? mergeFunction : undefined;

    const transferToContactAction = (
        extension: { key: string; contact: Extension; status: string },
        number: number,
    ) => {
        setSelectedPbxToTransferTo(null);
        setselectedExtensionToTransferTo(extension);
        setSelectedTransferNumber(number);
        setShowCallTransferDialog(true);
    };

    const transferToNumberAction = (number: number) => {
        setSelectedPbxToTransferTo(null);
        setselectedExtensionToTransferTo(null);
        setSelectedTransferNumber(number);
        setShowCallTransferDialog(true);
    };

    const transferToPbxAction = (pbx: CallTransferQueue, number: number) => {
        setselectedExtensionToTransferTo(null);
        setSelectedPbxToTransferTo(pbx);
        setSelectedTransferNumber(number);
        setShowCallTransferDialog(true);
    };

    const closeCallTransferDialog = () => {
        setShowCallTransferDialog(false);
    };

    const getSelectedTransferContact = () => {
        if (selectedExtensionToTransferTo) {
            return getSelectedExtensionTransferContact();
        } else if (selectedPbxToTransferTo) {
            return getSelectedPbxTransferContact();
        }
        return undefined;
    };
    const getSelectedPbxTransferContact = () => {
        if (selectedPbxToTransferTo.item) {
            return Map({
                key: selectedPbxToTransferTo.key,
                description: selectedPbxToTransferTo.item.get('description'),
            });
        }
        return Map();
    };

    const getSelectedExtensionTransferContact = () => {
        return selectedExtensionToTransferTo?.contact || Map();
    };

    const handleTransferBlind = () => {
        dispatch(
            transferBlindCall(selectedTransferNumber, transferLogLocation),
        );
        setShowCallTransferDialog(false);
    };

    const handleTransferAttended = () => {
        const transferContact = getSelectedTransferContact();

        dispatch(
            transferAttendedCall(
                selectedTransferNumber,
                transferContact,
                transferLogLocation,
            ),
        );
        setShowCallTransferDialog(false);
    };

    const extensionToTransferToIsInCall = () => {
        return (
            !!selectedExtensionToTransferTo &&
            isExtensionStatusInCall(selectedExtensionToTransferTo?.status)
        );
    };

    const remoteNumberFromPaiHeader = useAppSelector(getPaiHeaderFromLocalCall);
    let displayNumber = remoteNumberFromPaiHeader || remoteNumber;
    let isCoListening = false;

    if (session.direction === 'outgoing' && displayNumber?.startsWith('*28*')) {
        isCoListening = true;
        displayNumber = displayNumber.replace('*28*', '');
    }

    const transferInDoubleCallEnabled = useAppSelector(
        selectTransferInDoubleCallEnabled,
    );

    const userCountryCode = getCountryCode(ownContact, ownCustomer);

    displayNumber = convertNumberToE164(displayNumber, userCountryCode);
    const localNumber = convertNumberToE164(
        session.local_identity.uri.user,
        userCountryCode,
    );

    const hasMoreThanOneActiveCall = useAppSelector(
        (state) => getNumLocalCalls(state) > 1,
    );

    const canStartTransfer = transferInDoubleCallEnabled
        ? isAccepted && !isEnded
        : isAccepted && !isEnded && !hasMoreThanOneActiveCall;

    return (
        <>
            <CallItem
                callId={callId}
                direction={session.direction}
                remoteNumber={displayNumber}
                localNumber={localNumber}
                ownCountryCode={userCountryCode}
                startTime={startTime}
                isCoListening={isCoListening}
                isOnHold={!!isOnHold}
                isAccepted={!!isAccepted}
                isEnded={!!isEnded}
                failCause={failCause}
                isQueueCall={callIsQueueCall}
                hasNumberFromTop={hasNumberFromTop}
                onAnswer={onAnswer}
                onHangup={onHangup}
                onHold={onHold}
                onUnhold={onUnhold}
                onSwap={onSwap}
                onMerge={onMerge}
                onTransferStart={
                    canStartTransfer
                        ? () => {
                              openModal('numberPicker', {
                                  title: i18next.t('Call.Transfer'),
                                  contactSelectedAction:
                                      transferToContactAction,
                                  numberSelectedAction: transferToNumberAction,
                                  pbxSelectedAction: transferToPbxAction,
                              });
                          }
                        : undefined
                }
                spawnedNotifications={spawnedNotifications}
                onNotificationSpawned={onNotificationSpawned}
            />

            {showCallTransferDialog ? (
                <CallTransferDialog
                    transferNumber={selectedTransferNumber}
                    transferContact={getSelectedTransferContact()}
                    handleTransferBlind={handleTransferBlind}
                    handleTransferAttended={handleTransferAttended}
                    handleClose={closeCallTransferDialog}
                    thisExtensionIsUnavailable={extensionToTransferToIsInCall()}
                />
            ) : null}
        </>
    );
};

export default React.memo(LocalCall);
